import { RouterTransition } from '@app/components'
import {
  MantineProvider as BaseMantineProvider,
  type AnchorProps,
  type ButtonProps,
  type DefaultMantineColor,
  type InputLabelProps,
  type LoadingOverlayProps,
  type MantineThemeOverride,
  type ModalProps,
  type PaperProps,
  type StepProps,
  type ThemeIconProps,
  type Tuple
} from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { type NavigationProgressProps } from '@mantine/nprogress'
import localFont from 'next/font/local'
import { Work_Sans } from 'next/font/google'
import { type FC, type ReactNode } from 'react'

type ExtendedCustomColors = 'brand-light-blue' | 'brand-dark-blue' | 'brand-red' | 'brand-yellow' | DefaultMantineColor

declare module '@mantine/core' {
  // noinspection JSUnusedGlobalSymbols
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, Tuple<string, 10>>
  }
}

export const sanchezSlab = localFont({
  src: [
    {
      path: './fonts/SanchezSlab-Bold.otf',
      weight: '700',
      style: 'normal'
    },
    {
      path: './fonts/SanchezSlab-BoldItalic.otf',
      weight: '700',
      style: 'italic'
    },
    {
      path: './fonts/SanchezSlab-Black.otf',
      weight: '800',
      style: 'normal'
    },
    {
      path: './fonts/SanchezSlab-BlackItalic.otf',
      weight: '800',
      style: 'italic'
    }
  ]
})

export const workSans = Work_Sans({
  style: ['normal', 'italic'],
  subsets: ['latin'],
  weight: ['400', '500', '600', '700', '800', '900']
})

const theme: MantineThemeOverride = {
  colors: {
    'brand-dark-blue': [
      '#686984',
      '#5B5C7B',
      '#4F5074',
      '#44456D',
      '#393A68',
      '#2E3064',
      '#242661', // DEFAULT
      '#262752',
      '#262746',
      '#25263C'
    ],
    'brand-light-blue': [
      '#A7B4C2',
      '#8FA3B9',
      '#7794B4',
      '#5D86B3',
      '#4479B3',
      '#316EB0',
      '#1E63AE', // DEFAULT
      '#285A90',
      '#2D5178',
      '#2F4965'
    ],
    'brand-red': [
      '#E3DAD8',
      '#D4C2BF',
      '#C8ABA6',
      '#C1938C',
      '#BF7B70',
      '#C16252',
      '#C74833', // DEFAULT
      '#A64A3B',
      '#8C4A3F',
      '#774740'
    ],
    'brand-yellow': [
      '#FDFDFC',
      '#EDE9DE',
      '#E2D8BF',
      '#DCCA9F',
      '#DCC07D',
      '#E4BA56',
      '#F4B929', // DEFAULT
      '#DCA725',
      '#B68F31',
      '#987C38'
    ]
  },
  colorScheme: 'light',
  components: {
    Anchor: {
      defaultProps: {
        color: 'brand-dark-blue'
      } as AnchorProps
    },
    Button: {
      defaultProps: {
        color: 'brand-red',
        radius: 'lg',
        size: 'md'
      } as ButtonProps
    },
    Modal: {
      defaultProps: {
        centered: true,
        closeOnClickOutside: false,
        closeOnEscape: false,
        padding: 'lg',
        radius: 'lg'
      } as ModalProps
    },
    InputLabel: {
      defaultProps: {
        fw: '600'
      } as InputLabelProps
    },
    LoadingOverlay: {
      defaultProps: {
        loaderProps: {
          color: 'brand-light-blue',
          radius: 'lg'
        }
      } as LoadingOverlayProps
    },
    NavigationProgress: {
      defaultProps: {
        color: 'brand-light-blue'
      } as NavigationProgressProps
    },
    Paper: {
      defaultProps: {
        radius: 'lg'
      } as PaperProps
    },
    StepperStep: {
      defaultProps: {
        color: 'brand-dark-blue'
      } as StepProps
    },
    ThemeIcon: {
      defaultProps: {
        color: 'brand-dark-blue',
        radius: 'lg',
        size: 'lg',
        variant: 'filled'
      } as ThemeIconProps
    }
  },
  headings: {
    fontFamily: sanchezSlab.style.fontFamily,
    fontWeight: '800'
  },
  fontFamily: workSans.style.fontFamily,
  primaryShade: 6,
  globalStyles: (theme) => ({
    'html, body': {
      backgroundColor: theme.colors['brand-dark-blue'][6]
    }
  })
}

export interface MantineProviderProps {
  children: ReactNode
}

export const MantineProvider: FC<MantineProviderProps> = ({ children }) => (
  <BaseMantineProvider
    theme={theme}
    withGlobalStyles
    withNormalizeCSS
  >
    <RouterTransition />
    {children}
    <Notifications />
  </BaseMantineProvider>
)
